// src/lib/crg-ws-merge.js — row-level merge/diff helpers for crg-deals real-time sync.
//
// ── Почему этот модуль существует (инцидент 2026-06-12, data loss) ──
//
// Owner отредактировал CAP Rec в 3 строках CRG Deals → все 3 правки откатились.
// Форензика (audit + data/.history) показала две шестерни:
//
//   1. Echo-cascade: App-уровневый auto-save effect
//      `useEffect(() => debouncedSave('crg-deals', crgDeals), [crgDeals])`
//      шлёт ВЕСЬ массив из state вкладки на ЛЮБОЕ изменение state — включая
//      применение ЧУЖОГО WS-броадкаста после истечения skipSave-окна (skipSave
//      снимается через 2000ms, debounce-retry приходит через 2500ms — окна не
//      совпадают). Stale-вкладки cameron/zack/office «эхом» отстреливали свои
//      9-часовые снимки за секунды после сейва владельца — его новые значения
//      не доехали ни в одном сейве (единственная запись на диск была ОТКАТОМ
//      `.123 TR.capReceived: 2 → 1`).
//
//   2. Blind replace: WS-хендлер делал `setCrgDeals(msg.data)` — затирал
//      несохранённые локальные правки (debounce ещё не выстрелил).
//
// Лечение (frontend-only; server merge в backend/routes/crud-post.cjs:386-450 —
// row-level last-write-wins по validated updatedAt — КОРРЕКТЕН и не меняется):
//   - auto-save effect для crg-deals УДАЛЁН; каждый мутирующий хендлер шлёт
//     ТОЛЬКО изменённые строки через apiSaveRows (src/lib/api-client.js);
//   - WS-броадкаст применяется через mergeRowsServerWins (ниже) вместо
//     слепого replace.

/**
 * mergeRowsServerWins(prevRows, serverRows) → merged array (ALWAYS a fresh ref).
 *
 * Базис = serverRows (сервер авторитетен):
 *   - порядок и состав строк — серверные;
 *   - строки, которых нет на сервере, НЕ воскрешаются (удаления уважаются);
 *   - НО если в prev есть строка с тем же id и СТРОГО бОльшим updatedAt —
 *     оставляем локальную версию (локальная только-что-правка, сейв в полёте,
 *     броадкаст её ещё не содержит). Когда её сейв доедет, следующий броадкаст
 *     принесёт серверную копию с тем же/большим updatedAt — sticky-состояния нет.
 *
 * Свежая ссылка массива гарантирована (`serverRows.map(...)` всегда новый
 * массив) — это сохраняет re-render-гарантию, ради которой v31.80 вводил
 * blind replace (mergeByID+JSON.stringify путь тогда «терял» обновления UI).
 *
 * Equal updatedAt → серверная версия (защита от расхождения контента при
 * одинаковом ts: сервер — single source of truth).
 */
export function mergeRowsServerWins(prevRows, serverRows) {
  const server = Array.isArray(serverRows) ? serverRows : [];
  const prev = Array.isArray(prevRows) ? prevRows : [];
  if (prev.length === 0) return server.slice(); // fresh ref всё равно
  const prevById = new Map();
  for (const r of prev) {
    if (r && r.id != null) prevById.set(r.id, r);
  }
  return server.map(srvRow => {
    if (!srvRow || srvRow.id == null) return srvRow;
    const local = prevById.get(srvRow.id);
    if (!local) return srvRow;
    const localTs = Number(local.updatedAt) || 0;
    const serverTs = Number(srvRow.updatedAt) || 0;
    // СТРОГО больше — иначе сервер выигрывает (равные ts → сервер).
    // Jury A M2 (само-залечивание «sticky ghost»): локальная строка с бОльшим ts
    // переживает merge, даже если её сейв умер (rescue после 6 сбоев). Это НЕ
    // вечное расхождение: строка лежит в durable rescue-store + полной LS-таблице,
    // flushPendingSaves дошлёт её full-array путём при следующем mount/интервале —
    // сервер примет (ts новее), эхо принесёт serverTs === localTs → сервер победит.
    return localTs > serverTs ? local : srvRow;
  });
}

// 2026-10-07: diffRowsForSave (undo-дельта по снимку ВСЕЙ таблицы со штампом) удалён —
// такой откат перетирал чужие правки во всех строках после снимка. Построчный откат:
// src/lib/crg-revert.js.
